// MACHINERY V2 (Stroje) — the custom-machine form's data + behaviour (M2.7).
//
// Spec: data package §10 in full. Reference impl:
// components/MachineDialog.tsx (its `custom-form` mode) — this is that dialog's
// logic half. CustomMachineDialog.tsx / CustomMachineFields.tsx own the pixels
// (300-line cap). Artboards 0802 / 0803 / 0804.
//
// >>> §9 D7 — THE DIALOG NEVER EATS THE USER'S WORK. <<<
// v1 calls `onClose()` BEFORE `createMachine(...).unwrap()`
// (MachineDialog.tsx:370-371), so a rejection lands in the slice with the form
// already gone — data package §10 "States" records it as a gap. Here the create
// is awaited FIRST and `onCreated` (which closes) runs only on success. On
// failure the hook sets `submitError` and returns; every RHF value, the
// location and the whole image queue are untouched, and the dialog re-renders
// with a retry. This is D7's own sub-phase — the M2.5 R2 rule ("a dialog
// holding user work never discards it on failure") graduating to a standing one.
//
// >>> FIVE §10 QUIRKS ARE PRESERVED ON PURPOSE. DO NOT "FIX" THEM. <<<
//  1. axisConfigurationId CARRIES A NAME, NOT AN ID, until submit. See
//     customMachineOptions.ts axisConfigurationOptions for the full quote; the
//     resolution below is v1's MachineDialog.tsx:351.
//  2. `system` IS THE CONTROL-SYSTEM MODEL NAME (DTO System, a string) and is
//     DISABLED while controlSystemBrandId === the empty string — the exact
//     comparison of CncForm.tsx:236 / ProductionLineForm.tsx:232. That is why
//     the id defaults below are empty strings and not undefined: the comparison
//     is only meaningful against v1's own default shape
//     (MachineDialog.tsx:435-451, reproduced verbatim).
//  3. NEVER SEND AN EMPTY STRING FOR A GUID — a 400. Every id is resolved
//     through a find whose miss becomes undefined, and catalogModelId is
//     undefined by definition here (custom is the no-catalog-model branch, §12).
//  4. `version` and `description` have no inputs and are always coerced to
//     undefined; the legacy `image` is set to null (files[] superseded it).
//  5. A FIELD THE OTHER TYPE OWNS KEEPS ITS VALUE WHEN HIDDEN, exactly as v1:
//     react-hook-form 7.51.2 skips validation for an unmounted field
//     (validateField opens with "if (!mount || disabled) return {}" — read in
//     node_modules/react-hook-form/dist/index.cjs.js, minified there as
//     "if(!E||O)return{}") while shouldUnregister stays false, so the VALUE
//     survives and is submitted. Switching CNC to Linka and back therefore
//     behaves byte-for-byte like the live dialog.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { ChangeMachine } from "../../models/models";
import {
  createMachine,
  getAutomationLevels,
  getAxisConfigurations,
  getControlSystemBrands,
  getControlSystemModels,
  getManufacturers,
  getOperationTypes,
  getTypes,
  setError,
} from "../../customerMachinerySlice";
import { fetchLocations } from "../../customerLocationsSlice";
import { toastErrorMessage } from "../feedback/MachineryToastHost";
import { resolveAxisConfigurationId } from "./customMachineOptions";
import { MachineImage, filesFromGallery } from "./machineImages";

/** v1's create-mode defaults, field for field (MachineDialog.tsx:435-451). */
const CREATE_DEFAULTS: ChangeMachine = {
  model: "",
  serialNumber: "",
  typeId: "",
  manufacturerId: "",
  customBrandName: "",
  operationTypeId: "",
  axisConfigurationId: undefined,
  controlSystemBrandId: "",
  automationLevelId: undefined,
  numberOfStations: undefined,
  system: undefined,
  version: undefined,
  productionYear: undefined,
  description: undefined,
  catalogModelId: undefined,
  image: null,
};

export const useCustomMachineForm = (open: boolean, onCreated: (id?: string) => void) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation(["myMachinery", "common"]);

  const {
    types,
    manufacturers,
    operationTypes,
    axisConfigurations,
    controlSystemBrands,
    controlSystemModels,
    automationLevels,
  } = useAppSelector((s) => s.customerMachinery);
  const locations = useAppSelector((s) => s.customerLocations.locations);

  const form = useForm<ChangeMachine>({ defaultValues: CREATE_DEFAULTS });
  const { reset, setValue, watch } = form;

  /** Outside RHF, exactly as v1 keeps it (MachineDialog.tsx:152 and :165). */
  const [locationId, setLocationId] = useState<string | null>(null);
  const [images, setImagesState] = useState<MachineImage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  /**
   * >>> THE QUEUE'S AUTHORITY IS THE REF, NOT THE RENDER VALUE. <<<
   * Two readers need the queue from OUTSIDE a render: the submit handler RHF
   * captured, and the async accept pass in CustomMachineDialog (a FileReader
   * per file — machineImages.ts:133 — so its result lands ticks after it
   * started). A render-time `imagesRef.current = images` only ever tracks what
   * has already COMMITTED, which is why an overlapping accept could compute
   * `[...images, ...added]` from a stale array and drop the earlier batch,
   * clamp against a stale length (machineImages.ts:99) or resurrect a row the
   * user removed mid-read. Every mutation goes through `applyImages`: it writes
   * the ref FIRST, synchronously at the call, then the state. Readers use the
   * ref and are never behind.
   */
  const imagesRef = useRef(images);
  const applyImages = useCallback((next: MachineImage[]) => {
    imagesRef.current = next;
    setImagesState(next);
  }, []);

  const typeId = watch("typeId");
  const manufacturerId = watch("manufacturerId");
  const operationTypeId = watch("operationTypeId");
  const controlSystemBrandId = watch("controlSystemBrandId");

  // Bootstrap: the two lists this dialog cannot render without. The fleet page
  // already loads types (fleet/useFleetPageData.ts:115), so this is the guard
  // for every OTHER entry point, and it mirrors v1's own locations guard
  // (MachineDialog.tsx:155-160).
  useEffect(() => {
    if (!open) return;
    if (types.length === 0) dispatch(getTypes());
    if (locations.length === 0) dispatch(fetchLocations());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dispatch]);

  // Reactive option loads — v1's three effects (MachineDialog.tsx:465-485).
  //
  // >>> PLUS THE ONE THING v1 DOES NOT DO: CLEAR THE BRAND ON A TYPE SWITCH. <<<
  // Quirk 5 above (a hidden field keeps its value) is benign for a field the
  // new type also shows. For `manufacturerId` it is a DATA-INTEGRITY DEFECT:
  // `getManufacturers(typeId)` on the next line refetches the list per type, so
  // after CNC -> Production line the retained id is typically absent from
  // `manufacturers`. The Brand select then renders BLANK (FormSelect.tsx:120
  // resolves the label with `options.find(o => o.id === value)`), RHF's
  // `required` still PASSES because the stored string is non-empty, and the
  // submit resolver ships the id anyway — a `find` miss falls back to the raw
  // value (`|| model.manufacturerId ||`, v1's MachineDialog.tsx:360-361,
  // reproduced below). Net effect: the user sees an empty required field and
  // the server is sent the OTHER type's GUID.
  // Fixed HERE ONLY. v1's twin is tracked separately — §9 D4's "carried
  // features are rebuilt correctly, not ported broken" outranks preserve-the-
  // quirk when the quirk sends wrong data. Cleared to "" (not undefined) so the
  // field keeps CREATE_DEFAULTS' own shape; the resolver turns "" into
  // undefined, so quirk 3 ("never send '' for a Guid") still holds.
  useEffect(() => {
    if (!typeId) return;
    setValue("manufacturerId", "");
    dispatch(getManufacturers(typeId));
    dispatch(getOperationTypes(typeId));
    dispatch(getControlSystemBrands(typeId));
    dispatch(getAutomationLevels());
  }, [typeId, dispatch, setValue]);

  useEffect(() => {
    if (operationTypeId) dispatch(getAxisConfigurations(operationTypeId));
  }, [operationTypeId, dispatch]);

  useEffect(() => {
    if (controlSystemBrandId) dispatch(getControlSystemModels(controlSystemBrandId));
  }, [controlSystemBrandId, dispatch]);

  /** Back to the picker, or a fresh open, starts from v1's defaults again. */
  const resetAll = useCallback(() => {
    reset(CREATE_DEFAULTS);
    setLocationId(null);
    applyImages([]);
    setSubmitError(undefined);
  }, [reset, applyImages]);

  // Same reason as the image ref above, for the location the submit handler
  // reads. This one has no async writer, so tracking the render value is enough.
  const locationRef = useRef(locationId);
  locationRef.current = locationId;

  const onSubmit: SubmitHandler<ChangeMachine> = async (data) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(undefined);

    // v1's handleCreateMachine, NON-catalog branch (MachineDialog.tsx:349-369).
    const model: ChangeMachine = { ...data };
    model.locationId = locationRef.current || undefined;
    model.catalogModelId = undefined;
    model.operationTypeId =
      operationTypes.find((x) => x.id === model.operationTypeId)?.id || undefined;
    model.axisConfigurationId = resolveAxisConfigurationId(
      axisConfigurations,
      model.axisConfigurationId
    );
    model.controlSystemBrandId =
      controlSystemBrands.find((x) => x.id === model.controlSystemBrandId)?.id || undefined;
    model.automationLevelId = automationLevels.find((x) => x.id === model.automationLevelId)?.id;
    model.manufacturerId =
      manufacturers.find((x) => x.id === model.manufacturerId)?.id ||
      model.manufacturerId ||
      undefined;
    model.description = model.description ? model.description : undefined;
    model.productionYear = model.productionYear ? model.productionYear : undefined;
    model.system = model.system ? model.system : undefined;
    model.version = model.version ? model.version : undefined;
    model.image = null;
    model.files = filesFromGallery(imagesRef.current);

    try {
      const id = await dispatch(createMachine(model)).unwrap();
      // The rejected reducer (customerMachinerySlice.ts:211-213) writes `error`,
      // which MachineryToastHost renders. On THIS surface the error belongs in
      // the dialog the user is still looking at, so the slice copy is cleared —
      // otherwise D7's in-dialog notice and a toast would say the same thing
      // twice, and the toast would outlive the dialog.
      dispatch(setError(undefined));
      onCreated(id);
    } catch (rejected) {
      dispatch(setError(undefined));
      setSubmitError(toastErrorMessage(rejected, translate("myMachinery:v2.add.createFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  return {
    form,
    types,
    manufacturers,
    operationTypes,
    axisConfigurations,
    controlSystemBrands,
    controlSystemModels,
    automationLevels,
    locations,
    typeId,
    manufacturerId,
    controlSystemBrandId,
    locationId,
    setLocationId,
    images,
    /** Read the LIVE queue from an async callback — see applyImages above. */
    imagesRef,
    setImages: applyImages,
    submitting,
    submitError,
    resetAll,
    submit: form.handleSubmit(onSubmit),
  };
};

export default useCustomMachineForm;
